import { useEffect, useState } from 'react';
import { avisar, avisarErro } from './Avisos.tsx';
import { Icone } from './Icone.tsx';
import { JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarDialogo } from '../lib/dialogo.ts';
import { apiRelatorios, type EmailDeEntrega as Email } from '../lib/api';

/**
 * ⭐ **O E-MAIL DE ENTREGA, PRONTO PARA COPIAR — 16/10/2026 (PRD §53).**
 *
 * > *"Gostaria que, quando 'finalizarmos' o relatório, pudesse ter algum campo
 * > onde podemos copiar o 'ASSUNTO', o 'TEXTO' e o 'E-MAIL' a ser enviado. Com
 * > isso, sempre manteremos um padrão."* — Richard
 *
 * ⚠️ **A mesma janela dos textos do Comercial** (`ModelosDeAbordagem`): o mesmo
 * desenho, a mesma cópia com negrito, o mesmo aviso da hora. O texto vem
 * pronto do servidor (`lib/email-de-entrega.ts`); aqui só se mostra e se copia.
 *
 * ⚠️ **O Hub não envia nada.** Quem envia é a pessoa, do Gmail da casa, com o
 * PDF do botão "Relatório" anexo.
 */
export function EmailDeEntrega({ relatorioId, aoFechar }: { relatorioId: string; aoFechar: () => void }) {
  usarDialogo(aoFechar);
  const [email, setEmail] = useState<Email | null>(null);
  const [contatoId, setContatoId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    apiRelatorios
      .emailDeEntrega(relatorioId, contatoId)
      .then((e) => {
        if (vivo) setEmail(e);
      })
      .catch((e: unknown) => {
        if (vivo) setErro(e instanceof Error ? e.message : 'Não consegui montar o e-mail.');
      });
    return () => {
      vivo = false;
    };
  }, [relatorioId, contatoId]);

  const copiar = (texto: string, oQue: string) => {
    void navigator.clipboard
      .writeText(texto)
      .then(() => avisar(`${oQue} copiado.`))
      .catch(() => avisarErro('O navegador não deixou copiar. Selecione o texto e copie à mão.'));
  };

  /** O texto com o negrito do nome do devedor, e também sem ele: quem escolhe é onde se cola. */
  const copiarTexto = (e: Email) => {
    const item =
      typeof ClipboardItem === 'undefined'
        ? null
        : new ClipboardItem({
            'text/html': new Blob([e.html], { type: 'text/html' }),
            'text/plain': new Blob([e.texto], { type: 'text/plain' }),
          });
    if (!item || !navigator.clipboard?.write) {
      copiar(e.texto, 'Texto');
      return;
    }
    void navigator.clipboard
      .write([item])
      .then(() => avisar('Texto copiado, com o negrito.'))
      .catch(() => copiar(e.texto, 'Texto'));
  };

  const saudacao = email?.texto.match(/^[^,]+, (bom dia|boa tarde|boa noite)\./)?.[1] ?? null;
  const comEmail = (email?.contatos ?? []).filter((c) => c.email.trim());

  return (
    <JanelaEmBlocos
      rotulo="E-mail de entrega"
      sobrelinha="Relatório · entrega ao cliente"
      titulo="O e-mail de entrega"
      sub="No padrão da casa, já com o contato, o devedor e o código. O Hub monta; quem envia é você, do Gmail da casa, com o PDF anexo."
      largura="media"
      aoFechar={aoFechar}
      erro={erro}
      rodape={
        <>
          <button type="button" className="imv-bt fantasma" onClick={aoFechar}>Fechar</button>
          <span className="imv-esp" />
          {email?.para && (
            <button type="button" className="imv-bt fantasma" onClick={() => copiar(email.para, 'E-mail')}>
              <Icone nome="copiar" tamanho={13} /> Copiar o e-mail
            </button>
          )}
          {email && (
            <button type="button" className="imv-bt fantasma" onClick={() => copiar(email.assunto, 'Assunto')}>
              <Icone nome="copiar" tamanho={13} /> Copiar o assunto
            </button>
          )}
          {email && (
            <button type="button" className="imv-bt primario" onClick={() => copiarTexto(email)}>
              <Icone nome="copiar" tamanho={14} /> Copiar o texto
            </button>
          )}
        </>
      }
    >
      {!email ? (
        <p className="fp-vazio">Montando o e-mail…</p>
      ) : (
        <div className="mo-corpo">
          <div className="mo-assunto">
            <span className="imv-rotulo">Para</span>
            {email.para ? (
              <p>{email.para}</p>
            ) : (
              <p className="mo-faltam">
                <Icone nome="atencao" tamanho={14} />
                <span>
                  O cliente não tem contato com e-mail na ficha. Cadastre o contato (nome e e-mail) na{' '}
                  <a href={`/clientes/${email.clienteId}`}>ficha do cliente</a> e abra esta janela de novo.
                </span>
              </p>
            )}
            {comEmail.length > 1 && (
              <select
                aria-label="Enviar para"
                value={email.contatoId ?? ''}
                onChange={(ev) => setContatoId(ev.target.value)}
              >
                {comEmail.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome} · {c.email}
                    {c.principal ? ' (principal)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {saudacao && (
            <p className="mo-hora">
              <Icone nome="relogio" tamanho={13} />
              <span>
                Montado com <strong>{saudacao}</strong>, pela hora de agora. Se for enviar mais tarde, confira a saudação.
              </span>
            </p>
          )}

          <div className="mo-assunto">
            <span className="imv-rotulo">Assunto</span>
            <p>{email.assunto}</p>
          </div>

          <div className="mo-texto">
            <span className="imv-rotulo">Texto</span>
            <div className="mo-corrido">
              {email.paragrafos.map((par, i) => (
                <p key={i}>
                  {par.map((x, j) =>
                    /* ⚠️ A quebra de linha dentro do parágrafo ("Tudo bem?", "Atenciosamente,"). */
                    x.t.split('\n').map((linha, k) => (
                      <span key={`${j}-${k}`}>
                        {k > 0 && <br />}
                        {x.n ? <strong>{linha}</strong> : linha}
                      </span>
                    )),
                  )}
                </p>
              ))}
            </div>
          </div>
          <p className="mo-nota">
            Anexe o PDF gerado pelo botão "Relatório". A assinatura institucional não entra aqui: o Gmail a insere sozinho.
          </p>
        </div>
      )}
    </JanelaEmBlocos>
  );
}
